import { create } from 'zustand';
import { Drone, User } from '../types';

interface AppStore {
  user: User | null;
  token: string | null;
  setAuth: (user: User, token: string) => void;
  clearAuth: () => void;
  drones: Map<string, Drone>;
  paths: Map<string, Array<[number, number]>>;
  setDrone: (drone: Drone) => void;
  removeDrone: (droneId: string) => void;
  pruneStale: (maxAgeMs: number) => void;
  droneLastSeen: Map<string, number>;
  selectedDroneId: string | null;
  setSelectedDrone: (id: string | null) => void;
  mapCenter: [number, number];
  mapZoom: number;
  setMapCenter: (center: [number, number]) => void;
  setMapZoom: (zoom: number) => void;
  soundEnabled: boolean;
  setSoundEnabled: (v: boolean) => void;
  cloudMode: "ONLINE" | "AUTONOMOUS";
  tokenDaysLeft: number | null;
  tokenValid: boolean;
  syncPending: number;
  setCloudStatus: (s: { mode: "ONLINE" | "AUTONOMOUS"; tokenDaysLeft: number | null; tokenValid: boolean; syncPending: number }) => void;
  alarmedDrones: Map<string, "WARNING" | "DEFENSE">;
  setAlarmedDrones: (m: Map<string, "WARNING" | "DEFENSE">) => void;
  branding: { primaryColor: string; accentColor: string; companyName: string; logoUrl: string | null; faviconUrl: string | null } | null;
  setBranding: (b: AppStore['branding']) => void;
  activeTab: 'all' | 'position' | 'direction' | 'detection';
  setActiveTab: (tab: 'all' | 'position' | 'direction' | 'detection') => void;
}

const CENTER_LAT = parseFloat(process.env.REACT_APP_CENTER_LAT || '34.656');
const CENTER_LNG = parseFloat(process.env.REACT_APP_CENTER_LNG || '112.353');

export const useStore = create<AppStore>((set) => ({
  user: (() => {
    try { return JSON.parse(localStorage.getItem('uav_user') || 'null'); } catch { return null; }
  })(),
  token: localStorage.getItem('uav_token'),
  setAuth: (user, token) => {
    localStorage.setItem('uav_token', token);
    localStorage.setItem('uav_user', JSON.stringify(user));
    set({ user, token });
  },
  clearAuth: () => {
    localStorage.removeItem('uav_token');
    localStorage.removeItem('uav_user');
    set({ user: null, token: null, drones: new Map(), paths: new Map() });
  },
  drones: new Map(),
  paths: new Map(),
  droneLastSeen: new Map(),
  setDrone: (drone) => set((state) => {
    const nextDrones = new Map(state.drones);
    nextDrones.set(drone.droneId, drone);

    const nextPaths = new Map(state.paths);
    if (drone.lat && drone.lng) {
      const existing = nextPaths.get(drone.droneId) || [];
      nextPaths.set(drone.droneId, [...existing, [drone.lat, drone.lng] as [number, number]].slice(-20));
    }

    const nextLastSeen = new Map(state.droneLastSeen);
    nextLastSeen.set(drone.droneId, Date.now());
    return { drones: nextDrones, paths: nextPaths, droneLastSeen: nextLastSeen };
  }),
  removeDrone: (droneId) => set((state) => {
    const nextDrones = new Map(state.drones);
    nextDrones.delete(droneId);
    const nextPaths = new Map(state.paths);
    nextPaths.delete(droneId);
    const nextLastSeen2 = new Map(state.droneLastSeen);
    nextLastSeen2.delete(droneId);
    return {
      drones: nextDrones,
      paths: nextPaths,
      selectedDroneId: state.selectedDroneId === droneId ? null : state.selectedDroneId,
      droneLastSeen: nextLastSeen2,
    };
  }),
  pruneStale: (maxAgeMs) => set((state) => {
    const now = Date.now();
    const nextDrones = new Map(state.drones);
    const nextPaths = new Map(state.paths);
    const nextLastSeen3 = new Map(state.droneLastSeen);
    for (const [id, ts] of nextLastSeen3.entries()) {
      if (now - ts > maxAgeMs) {
        nextDrones.delete(id);
        nextPaths.delete(id);
        nextLastSeen3.delete(id);
      }
    }
    return { drones: nextDrones, paths: nextPaths, droneLastSeen: nextLastSeen3 };
  }),
  selectedDroneId: null,
  setSelectedDrone: (id) => set({ selectedDroneId: id }),
  mapCenter: (() => {
    try {
      const saved = localStorage.getItem("uav_map_center");
      if (saved) { const p = JSON.parse(saved); if (Array.isArray(p) && p.length===2) return p as [number,number]; }
    } catch {}
    return [48.310, 18.084] as [number, number];
  })(),
  mapZoom: 14,
  setMapCenter: (center) => {
    try { localStorage.setItem("uav_map_center", JSON.stringify(center)); } catch {}
    set({ mapCenter: center });
  },
  setMapZoom: (zoom) => set({ mapZoom: zoom }),
  soundEnabled: true,
  setSoundEnabled: (v) => set({ soundEnabled: v }),
  cloudMode: "ONLINE",
  tokenDaysLeft: null,
  tokenValid: true,
  syncPending: 0,
  setCloudStatus: (s) => set({ cloudMode: s.mode, tokenDaysLeft: s.tokenDaysLeft, tokenValid: s.tokenValid, syncPending: s.syncPending }),
  alarmedDrones: new Map(),
  setAlarmedDrones: (m) => set({ alarmedDrones: m }),
  activeTab: 'all',
  setActiveTab: (tab) => set({ activeTab: tab }),
  branding: null,
  setBranding: (b) => set({ branding: b }),
}));
